import {
  CoffeeRustAlert,
  CoffeeRustAlertResponse,
  CoffeeRustAlertPaginatedResponse,
  CoffeeRustAlertSummary,
  Station,
  CalculationData,
} from '../models/coffee-rust-alert';

/**
 * Adapter para transformar dados da API de alertas de ferrugem do café
 */
export class CoffeeRustAlertAdapter {
  /**
   * Converte resposta da API para CoffeeRustAlert
   */
  static fromApi(data: any): CoffeeRustAlert {
    return {
      station: {
        id: Number(data.station?.id) || 0,
        name: data.station?.name || '',
        latitude: Number(data.station?.latitude) || 0,
        longitude: Number(data.station?.longitude) || 0,
      },
      modelName: data.modelName || '',
      noticeDate: data.noticeDate || '',
      incidenceDate: data.incidenceDate || '',
      riskClassification: data.riskClassification || '',
      calculatedValue: Number(data.calculatedValue) || 0,
      dataCount: Number(data.dataCount) || 0,
      calculationData: Array.isArray(data.calculationData)
        ? data.calculationData.map((item: any) =>
            this.fromApiCalculationData(item),
          )
        : [],
    };
  }

  /**
   * Converte dados de cálculo da API
   */
  static fromApiCalculationData(data: any): CalculationData {
    return {
      identifier: data.identifier || '',
      value: Number(data.value) || 0,
    };
  }

  /**
   * Converte array de dados da API para array de CoffeeRustAlert
   */
  static fromApiArray(dataArray: any[]): CoffeeRustAlert[] {
    if (!Array.isArray(dataArray)) {
      return [];
    }
    return dataArray.map((data) => this.fromApi(data));
  }

  /**
   * Converte resposta completa da API
   */
  static fromApiResponse(data: any): CoffeeRustAlertResponse {
    return {
      favorableAndVeryFavorableAlertsCount:
        Number(data.favorableAndVeryFavorableAlertsCount) || 0,
      nonFavorableAlertsCount: Number(data.nonFavorableAlertsCount) || 0,
      nonFavorableAlerts: this.fromApiArray(data.nonFavorableAlerts || []),
      favorableAndVeryFavorableAlerts: this.fromApiArray(
        data.favorableAndVeryFavorableAlerts || [],
      ),
    };
  }

  /**
   * Converte resposta paginada da API
   */
  static fromApiPaginatedResponse(data: any): CoffeeRustAlertPaginatedResponse {
    return {
      content: this.fromApiArray(data.content || []),
      totalElements: Number(data.totalElements) || 0,
      totalPages: Number(data.totalPages) || 0,
      currentPage: Number(data.currentPage) || 0,
      hasNext: Boolean(data.hasNext),
      hasPrevious: Boolean(data.hasPrevious),
      favorableAndVeryFavorableAlertsCount:
        Number(data.favorableAndVeryFavorableAlertsCount) || 0,
      nonFavorableAlertsCount: Number(data.nonFavorableAlertsCount) || 0,
    };
  }

  /**
   * Cria resumo a partir da resposta da API
   */
  static createSummary(
    response: CoffeeRustAlertResponse,
  ): CoffeeRustAlertSummary {
    return {
      totalAlerts:
        response.favorableAndVeryFavorableAlertsCount +
        response.nonFavorableAlertsCount,
      favorableCount: response.favorableAndVeryFavorableAlertsCount,
      nonFavorableCount: response.nonFavorableAlertsCount,
      lastUpdate: new Date().toISOString(),
    };
  }

  /**
   * Mapeia classificação de risco para cor/classe CSS
   */
  static getRiskClassificationClass(riskClassification: string): string {
    const classMap: { [key: string]: string } = {
      'não favorável': 'danger',
      'nao favoravel': 'danger',
      favorável: 'warning',
      favoravel: 'warning',
      'muito favorável': 'success',
      'muito favoravel': 'success',
    };
    return classMap[riskClassification.toLowerCase()] || 'secondary';
  }

  /**
   * Mapeia classificação de risco para ícone
   */
  static getRiskClassificationIcon(riskClassification: string): string {
    const iconMap: { [key: string]: string } = {
      'não favorável': 'fa-check-circle',
      'nao favoravel': 'fa-check-circle',
      favorável: 'fa-exclamation-circle',
      favoravel: 'fa-exclamation-circle',
      'muito favorável': 'fa-exclamation-triangle',
      'muito favoravel': 'fa-exclamation-triangle',
    };
    return iconMap[riskClassification.toLowerCase()] || 'fa-info-circle';
  }

  /**
   * Obtém valor de um identificador específico dos dados de cálculo
   */
  static getCalculationDataValue(
    alert: CoffeeRustAlert,
    identifier: string,
  ): number | null {
    const data = alert.calculationData.find(
      (item) => item.identifier === identifier,
    );
    return data ? data.value : null;
  }

  /**
   * Obtém temperatura mínima média do alerta
   */
  static getAvgMinTemp(alert: CoffeeRustAlert): number | null {
    return this.getCalculationDataValue(alert, 'avgMinTemp');
  }

  /**
   * Obtém umidade média do alerta
   */
  static getAvgHumidity(alert: CoffeeRustAlert): number | null {
    return this.getCalculationDataValue(alert, 'avgHumidity');
  }
}
